import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { QRCodeScanData } from '../../../../store/models/analytics/QRCodeScans';
import { TopicMap } from '../../../../store/slices/topicSlice';
import { colors } from '../../../../theme/palette';
import { ChartContent } from './ChartContent';

const useStyles = makeStyles({
	tableHeader: {
		backgroundColor: colors.grayDark,
		position: 'sticky',
		top: 0,
		zIndex: 1,
	},
	headerCell: {
		color: colors.pureWhite,
		borderBottom: `2px solid ${colors.grayDark20}`,
		padding: '8px 16px',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: colors.grayDark20,
		},
	},
	headerCellSortable: {
		color: colors.pureWhite,
		borderBottom: `2px solid ${colors.grayDark20}`,
		padding: '2px 16px',
		cursor: 'pointer',
		position: 'relative',
		'&:hover': {
			backgroundColor: colors.grayDark20,
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			right: '10px',
			top: '50%',
			transform: 'translateY(-50%)',
			width: '0',
			height: '0',
			borderLeft: '4px solid transparent',
			borderRight: '4px solid transparent',
		},
	},
	headerCellSortedAsc: {
		'&::after': {
			borderBottom: '4px solid white',
		},
	},
	headerCellSortedDesc: {
		'&::after': {
			borderTop: '4px solid white',
		},
	},
	row: {
		'&:nth-of-type(odd)': {
			backgroundColor: colors.grayDark5,
		},
		'&:nth-of-type(even)': {
			backgroundColor: colors.pureWhite,
		},
		'&:hover': {
			backgroundColor: colors.grayLight20,
		},
	},
	cell: {
		color: colors.grayDark,
		padding: '5px 16px',
		borderBottom: `1px solid ${colors.grayDark20}`,
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	qrCodeNameCell: {
		color: colors.grayDark,
		padding: '2px 16px',
		borderBottom: `1px solid ${colors.grayDark20}`,
		maxWidth: '250px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	pageNameCell: {
		color: colors.grayDark,
		padding: '5px 16px',
		borderBottom: `1px solid ${colors.grayDark20}`,
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	totalCell: {
		color: colors.grayDark,
		borderRadius: 4,
		borderBottom: `1px solid ${colors.grayDark20}`,
		padding: '5px 16px',
		minWidth: '100px',
		width: '100px',
	},
});

interface ScanByCodeProps {
	qrCodeScanData: QRCodeScanData[];
	topics: TopicMap;
}

const getTopicName = (topic: any) => {
	if (!topic) return '';
	const en = topic.name.find((n: any) => n.language === 'en');
	return en ? en.name : topic.name[0]?.name || '';
};

type SortDirection = 'asc' | 'desc' | null;
type SortField = 'qrCodeName' | 'totalScans' | null;

export const ScanByCode: React.FC<ScanByCodeProps> = ({ qrCodeScanData, topics }) => {
	const classes = useStyles();
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);
	const [sortField, setSortField] = useState<SortField>(null);

	const handleSort = (field: SortField) => {
		if (sortField !== field) {
			setSortField(field);
			setSortDirection('asc');
		} else if (sortDirection === 'asc') {
			setSortDirection('desc');
		} else if (sortDirection === 'desc') {
			setSortField(null);
			setSortDirection(null);
		}
	};

	const sortedData = useMemo(() => {
		if (!sortDirection || !sortField) return qrCodeScanData;

		return [...qrCodeScanData].sort((a, b) => {
			if (sortField === 'qrCodeName') {
				const nameA = a.qrCodeName.toLowerCase();
				const nameB = b.qrCodeName.toLowerCase();

				if (sortDirection === 'asc') {
					return nameA.localeCompare(nameB);
				} else {
					return nameB.localeCompare(nameA);
				}
			} else if (sortField === 'totalScans') {
				const totalA = a.scans.reduce((sum, s) => sum + s.value, 0);
				const totalB = b.scans.reduce((sum, s) => sum + s.value, 0);

				if (sortDirection === 'asc') {
					return totalA - totalB;
				} else {
					return totalB - totalA;
				}
			}
			return 0;
		});
	}, [qrCodeScanData, sortDirection, sortField]);

	const getHeaderCellClass = (field: SortField) => {
		if (sortField !== field) {
			return classes.headerCellSortable;
		}

		let className = classes.headerCellSortable;
		if (sortDirection === 'asc') {
			className += ` ${classes.headerCellSortedAsc}`;
		} else if (sortDirection === 'desc') {
			className += ` ${classes.headerCellSortedDesc}`;
		}
		return className;
	};

	return (
		<ChartContent
			name={'SCAN BY CODE'}
			toolTipMsg={'The number of scans for each QR code (default and named QR codes).'}
		>
			<TableContainer
				component={Paper}
				style={{
					width: '100%',
					height: '300px',
					borderRadius: 0,
					boxShadow: 'none',
					marginTop: '10px',
					paddingBottom: '30px',
					overflowX: 'hidden',
				}}
			>
				<Table style={{ tableLayout: 'fixed', width: '100%' }}>
					<TableHead>
						<TableRow className={classes.tableHeader}>
							<TableCell
								className={getHeaderCellClass('qrCodeName')}
								onClick={() => handleSort('qrCodeName')}
								style={{ width: '50%' }}
							>
								QR Code Name
							</TableCell>
							<TableCell className={classes.headerCell} style={{ width: '35%' }}>
								Page Name
							</TableCell>
							<TableCell
								align="right"
								className={getHeaderCellClass('totalScans')}
								onClick={() => handleSort('totalScans')}
								style={{ width: '15%' }}
							>
								Total
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody style={{ overflow: 'auto' }}>
						{sortedData.map((qrCode) => {
							const topicId = qrCode.topicId;
							const topic = topics[topicId];
							const topicName = getTopicName(topic);
							const totalScans = qrCode.scans.reduce((sum, s) => sum + s.value, 0);
							// Display "Default QR Code" for better UX when name is "default"
							const displayName = qrCode.qrCodeName === 'default' ? 'Default QR Code' : qrCode.qrCodeName;
							return (
								<TableRow key={qrCode.qrCodeId} className={classes.row}>
									<TableCell className={classes.qrCodeNameCell} title={displayName}>
										{displayName}
									</TableCell>
									<TableCell className={classes.pageNameCell} title={topicName}>
										{topicName}
									</TableCell>
									<TableCell align="right" className={classes.totalCell}>
										{totalScans}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</ChartContent>
	);
};
